import {
  Card,
  CardBody,
  CardColors,
  CardDetails,
  CardFooterItem,
  CardSummary,
} from "$components/card";
import { FormattedDate } from "$components/date/formatted-date";
import { CardHeader } from "$routes/patient/_lib/history-card";
import DiagnosisList, {
  DIAGNOSIS_TITLES,
  type DiagnosisListProps,
} from "$routes/patient/[:patientId]/inpatient/_lib/details/diagnosis-list";
import { linkToPatientInpatient } from "$routes/patient/[:patientId]/inpatient/links";
import { Box, makeStyles } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { Skeleton } from "@material-ui/lab";
import type { VisitCareEvent } from "elephant-web/gql";
import { defineMessages, useIntl } from "react-intl";
import { Link } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
  inpatientDetailsIcon: {
    fontSize: 24,
    transform: "rotate(-90deg)",
  },
  linkText: {
    textDecoration: "none",
  },
  placeholderCard: {
    minHeight: "163px",
  },
  skeleton: {
    marginTop: theme.spacing(4),
  },
}));

export const InpatientCardPlaceholderContainer = ({ children }) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  return (
    <Card color={CardColors.PRIMARY} className={classes.placeholderCard}>
      <CardHeader
        color={CardColors.PRIMARY}
        category={formatMessage({
          id: "inpatient.history.placeholder.title",
          defaultMessage: "Inpatient",
        })}
      />
      {children}
    </Card>
  );
};

export const InpatientCardPlaceholder = () => {
  const classes = useStyles();
  return (
    <InpatientCardPlaceholderContainer>
      <Box className={classes.skeleton}>
        <Skeleton variant="text" animation="wave" />
        <Skeleton variant="text" animation="wave" />
        <Skeleton variant="text" animation="wave" />
      </Box>
    </InpatientCardPlaceholderContainer>
  );
};

const PERFORMER_TITLES = defineMessages({
  admission: {
    id: "inpatientCard.performer.title.admission",
    defaultMessage: "Admitted by",
  },
  discharge: {
    id: "inpatientCard.performer.title.discharge",
    defaultMessage: "Discharged by",
  },
});

const EventSummary = ({
  diagnosisTitle,
  performerTitle,
  event,
}: {
  diagnosisTitle: DiagnosisListProps["title"];
  performerTitle: DiagnosisListProps["title"];
  event: VisitCareEvent;
}) => {
  const { formatMessage } = useIntl();

  return (
    <>
      <Box marginBottom={0}>
        <DiagnosisList
          title={diagnosisTitle}
          diagnosisList={event?.diagnosisList}
        />
      </Box>
      <Box>
        <CardFooterItem>
          {formatMessage(performerTitle)}
          {": "}
          {event.performer?.name}
          {", "}
          <FormattedDate showTime value={event.created} />
        </CardFooterItem>
      </Box>
    </>
  );
};

export interface InpatientCardProps {
  patientUuid?: string;
  visitId: string;
  admissionEvent?: VisitCareEvent;
  dischargeEvent?: VisitCareEvent;
}

const InpatientCard = ({
  patientUuid,
  visitId,
  admissionEvent,
  dischargeEvent,
  ...props
}: InpatientCardProps) => {
  const classes = useStyles();
  const inpatientDetailsLink = linkToPatientInpatient(patientUuid, visitId);
  const { formatMessage } = useIntl();

  return (
    <Link to={inpatientDetailsLink} className={classes.linkText} {...props}>
      <Card color={CardColors.PRIMARY}>
        <CardBody>
          <CardSummary>
            <Box display="flex" alignItems="center">
              <Box flex={1}>
                <CardHeader
                  color={CardColors.PRIMARY}
                  category={formatMessage({
                    id: "inpatient.history.title",
                    defaultMessage: "Inpatient",
                  })}
                  id={visitId}
                />
              </Box>
              <ExpandMoreIcon
                className={classes.inpatientDetailsIcon}
                color="action"
              />
            </Box>
          </CardSummary>
          <CardDetails>
            {admissionEvent ? (
              <EventSummary
                event={admissionEvent}
                diagnosisTitle={DIAGNOSIS_TITLES.admission}
                performerTitle={PERFORMER_TITLES.admission}
              />
            ) : null}
            {dischargeEvent ? (
              <EventSummary
                event={dischargeEvent}
                diagnosisTitle={DIAGNOSIS_TITLES.discharge}
                performerTitle={PERFORMER_TITLES.discharge}
              />
            ) : null}
          </CardDetails>
        </CardBody>
      </Card>
    </Link>
  );
};

export default InpatientCard;
